<script lang="ts">
  /**
   * The demo redaction switch — the visible half of `lib/privacy.svelte.ts`.
   *
   * SUPERADMIN ONLY, gated by its caller (`AdminShell`). A creator looking at their
   * own community has nobody to hide it from and would only be puzzled by a control
   * that smears their dashboard.
   *
   * THE OFF AND ON STATES ARE DELIBERATELY LOPSIDED. Off, this is the quietest thing
   * in the header: an outline icon at chrome weight, because 99% of the time it is not
   * what anyone is here for. On, it fills solid primary and the eye picks up a strike
   * through it — loud, because the one failure this feature can have that MATTERS is
   * believing it is on when it is not, and the other is leaving it on and wondering
   * why the panel looks broken.
   *
   * ICON-ONLY, AND THE SAME SIZE IN BOTH STATES. It first carried the word "Private"
   * when engaged, which cost two things: the sidebar brand beside it truncated to
   * "Sid…", and the header re-laid-out on every toggle. A control that changes width
   * when you press it makes the chrome jitter in the middle of a recording. The word
   * is not what carries the signal anyway — `app.css` draws a primary hairline across
   * the top of the viewport for as long as the mode is on, which is visible from
   * anywhere in the app and cannot be squeezed out by a narrow rail.
   *
   * It is a `<button>` with `aria-pressed`, not a checkbox styled as a pill: it acts
   * immediately on click and has no form to belong to.
   */
  import { privacy, togglePrivate } from '../privacy.svelte';

  // The label says what a CLICK WILL DO, which is the opposite of the current state.
  const label = $derived(privacy.on ? 'Show real data' : 'Blur sensitive data');
</script>

<button
  type="button"
  onclick={togglePrivate}
  aria-pressed={privacy.on}
  aria-label={label}
  title="{label} (⌘⇧B)"
  class="grid h-7 w-7 shrink-0 place-items-center rounded-field border transition-colors
         {privacy.on
    ? 'border-primary bg-primary text-primary-content'
    : 'hair text-base-content/55 hover:bg-base-200 hover:text-base-content'}"
>
  {#if privacy.on}
    <!-- Eye, struck through: the mode is hiding things. -->
    <svg
      class="h-4 w-4 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M6.61 6.61A18.4 18.4 0 0 0 2 12s3 8 10 8a9.1 9.1 0 0 0 5.39-1.61" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  {:else}
    <svg
      class="h-4 w-4 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  {/if}
</button>
